import { buildApiUrl, createHeaders } from "./apiClient";

const getCurrentUserId = () => {
  const direct =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");
  if (direct && !Number.isNaN(Number(direct))) return Number(direct);
  try {
    const raw =
      localStorage.getItem("userprofile") ||
      sessionStorage.getItem("userprofile");
    if (raw) {
      const u = JSON.parse(raw);
      return u?.id || u?.user_id || u?.userId || u?.user?.id || null;
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const fetchIncomingConnectionRequests = async ({
  status = "pending",
  limit = 50,
} = {}) => {
  const userId = getCurrentUserId();
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  const res = await fetch(
    buildApiUrl(`/follow/requests/incoming?${params.toString()}`),
    {
      method: "GET",
      headers: createHeaders({ userId }),
    },
  );
  if (!res.ok) throw new Error(`Failed to load invitations (${res.status})`);
  return res.json();
};

export const acceptConnectionRequest = async (requestId) => {
  const userId = getCurrentUserId();
  const res = await fetch(
    buildApiUrl(`/follow/requests/${requestId}/accept`),
    {
      method: "POST",
      headers: createHeaders({ userId }),
      body: JSON.stringify({}),
    },
  );
  if (!res.ok) throw new Error(`Failed to accept request (${res.status})`);
  return res.json();
};

export const rejectConnectionRequest = async (requestId) => {
  const userId = getCurrentUserId();
  const res = await fetch(
    buildApiUrl(`/follow/requests/${requestId}/reject`),
    {
      method: "POST",
      headers: createHeaders({ userId }),
      body: JSON.stringify({}),
    },
  );
  if (!res.ok) throw new Error(`Failed to reject request (${res.status})`);
  return res.json();
};

/**
 * Withdraw an outbound pending connection request addressed to `requestedId`.
 * The backend looks up the pending row for (me, requestedId) and deletes it.
 */
export const withdrawConnectionRequest = async (requestedId) => {
  const userId = getCurrentUserId();
  const res = await fetch(buildApiUrl(`/follow/request`), {
    method: "DELETE",
    headers: createHeaders({ userId }),
    body: JSON.stringify({ requested_id: Number(requestedId) }),
  });
  if (!res.ok) throw new Error(`Failed to withdraw request (${res.status})`);
  return res.json();
};

/**
 * Send a connection request to another user.
 * POST /follow/request with { requester_id, requested_id }
 */
export const sendConnectionRequest = async (requestedId) => {
  const userId = getCurrentUserId();
  if (!userId) throw new Error("Not logged in");
  const res = await fetch(buildApiUrl(`/follow/request`), {
    method: "POST",
    headers: createHeaders({ userId }),
    body: JSON.stringify({
      requester_id: Number(userId),
      requested_id: Number(requestedId),
    }),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.error || data.message || detail;
    } catch (_) {}
    throw new Error(detail);
  }
  return res.json();
};

/**
 * Check the connection-request status between the current user and `requestedId`.
 * GET /follow/request/status?requested_id={id}
 * Returns the status string ("pending", "accepted", etc.) or null if none.
 */
export const checkConnectionStatus = async (requestedId) => {
  const userId = getCurrentUserId();
  if (!userId) return null;
  const params = new URLSearchParams({ requested_id: String(requestedId) });
  const res = await fetch(
    buildApiUrl(`/follow/request/status?${params.toString()}`),
    {
      method: "GET",
      headers: createHeaders({ userId }),
    },
  );
  if (!res.ok) return null;
  try {
    const data = await res.json();
    return data.status || null;
  } catch {
    return null;
  }
};

/**
 * The other member's id in a connection row. /follow/followees nests them
 * under `user`; the flatter shapes below come from adjacent endpoints and
 * from older responses. Reading only the flat ones — as this file used to —
 * yields nothing at all, which reads as "not connected" everywhere it is
 * asked.
 */
export const followeeUserId = (row) =>
  row?.user?.id ??
  row?.profile?.user_id ??
  row?.profile?.id ??
  row?.followee_id ??
  row?.user_id ??
  row?.id ??
  (typeof row === "number" ? row : null);

/**
 * Fetch the list of user IDs the current user is following (connected to).
 * GET /follow/followees?user_id={id}&limit=500
 * Returns an array of followee user-ID numbers.
 */
export const fetchFolloweeIds = async () => {
  const userId = getCurrentUserId();
  if (!userId) return [];
  const params = new URLSearchParams({
    user_id: String(userId),
    limit: "500",
  });
  const res = await fetch(
    buildApiUrl(`/follow/followees?${params.toString()}`),
    {
      method: "GET",
      headers: createHeaders({ userId }),
    },
  );
  if (!res.ok) return [];
  try {
    const data = await res.json();
    const rows = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
      ? data
      : [];
    return rows
      .map((r) => Number(followeeUserId(r)))
      .filter((id) => Number.isFinite(id) && id > 0);
  } catch {
    return [];
  }
};

/**
 * Remove an existing connection symmetrically — the backend now deletes the
 * follow edge in both directions and clears any historical request row.
 */
export const removeConnection = async (peerUserId) => {
  const userId = getCurrentUserId();
  const res = await fetch(buildApiUrl(`/follow`), {
    method: "DELETE",
    headers: createHeaders({ userId }),
    body: JSON.stringify({ followee_id: Number(peerUserId) }),
  });
  if (!res.ok) throw new Error(`Failed to remove connection (${res.status})`);
  return res.json();
};
